import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  FileDown, TrendingUp, History, Target, Map, LayoutDashboard, 
  RefreshCcw, ArrowUpRight, ArrowDownRight 
} from 'lucide-react';

// Service Imports
import { fetchCountyAnalysis, fetchSubCountyAnalysis } from '../services/analysisService';
import { fetchSubcounties } from '../services/subcountyService';
import { exportDashboardToPDF } from '../utils/exportUtils';
import { fetchRoNamesBySubCounty } from '../utils/userFilters';
import { getHeatmapClass, calculatePerformance, getBarColor, getBarColorOnly } from '../utils/analysisUtils';

const RevenueExecutiveDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState({}); 
    const [reportDataPrev, setReportDataPrev] = useState({}); 
    const [scRankings, setScRankings] = useState([]);
    const [nextRefresh, setNextRefresh] = useState(10); // minutes remaining
    const dashboardRef = useRef(null);
 
    const now = new Date();
    const currentFYStart = now.getMonth() + 1 >= 7 ? now.getFullYear() : now.getFullYear() - 1;
    const currentFYStartPrev = currentFYStart - 1;
    const loadDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const countyRaw = await fetchCountyAnalysis(currentFYStart);
            const groupedCounty = countyRaw.reduce((acc, row) => {
                if (!acc[row.rev_stream]) acc[row.rev_stream] = [];
                acc[row.rev_stream].push(row);
                return acc;
            }, {});
            setReportData(groupedCounty);
            const countyRawPrev = await fetchCountyAnalysis(currentFYStartPrev);
            const groupedCountyPrev = countyRawPrev.reduce((acc, row) => {
                if (!acc[row.rev_stream]) acc[row.rev_stream] = [];
                acc[row.rev_stream].push(row);
                return acc;
            }, {});
            setReportDataPrev(groupedCountyPrev);

                const scList = await fetchSubcounties();
                const rankingPromises = scList.map(async (sc) => {
                // Fetch performance data and staff list in parallel for efficiency
                const [scData, staff] = await Promise.all([
                    fetchSubCountyAnalysis(currentFYStart, sc.id),
                    fetchRoNamesBySubCounty(sc.id)
                ]);
                const totalActual = scData.reduce((sum, row) => sum + Number(row.amount_fy0 || 0), 0);
                const totalTarget = scData.reduce((sum, row) => sum + Number(row.monthly_target || 0), 0);

                return {
                    id: sc.id,
                    name: sc.name,
                    actual: totalActual,
                    target: totalTarget,
                    staff: staff, // Store the array of usernames here
                    performance: calculatePerformance(totalActual, totalTarget)
                };
            });

            const resolvedRankings = await Promise.all(rankingPromises);
            setScRankings(resolvedRankings.sort((a, b) => b.performance - a.performance));
        } catch (err) {
            console.error("Dashboard Load Error:", err);
        } finally {
            setLoading(false);
        }
    }, [currentFYStart, currentFYStartPrev]);

    useEffect(() => {
        loadDashboardData();
    }, [currentFYStart, currentFYStartPrev, loadDashboardData]);

    const currentTotal = Object.values(reportData).flat().reduce((s, r) => s + Number(r.amount_fy0 || 0), 0);
    const previousTotal = Object.values(reportDataPrev).flat().reduce((s, r) => s + Number(r.amount_fy0 || 0), 0);
    const globalTarget = scRankings.reduce((s, r) => s + r.target, 0);
    const globalPerformance = calculatePerformance(currentTotal, globalTarget);
    const variance = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;

    const AllStreams = Object.entries(reportData).map(([name, records]) => {
        const amount = records.reduce((sum, r) => sum + Number(r.amount_fy0 || 0), 0);
        return { name, amount, percentage: currentTotal > 0 ? (amount / currentTotal) * 100 : 0 };
    }).sort((a, b) => b.amount - a.amount);
    
    //const topStreams = AllStreams.slice(0, 5);

    // Add this inside the RevenueExecutiveDashboard component
    useEffect(() => {
        // 1. Define the 10-minute interval (10 minutes * 60 seconds * 1000 milliseconds)
        const REFRESH_INTERVAL = 10 * 60 * 1000;

        const intervalId = setInterval(() => {
            console.log("Auto-refreshing Executive Dashboard data...");
            loadDashboardData();
        }, REFRESH_INTERVAL);

        // 2. Cleanup: Clear the interval if the component unmounts
        return () => {
            clearInterval(intervalId);
            console.log("Executive Dashboard auto-refresh stopped.");
        };
    }, [loadDashboardData]); // loadDashboardData is stable due to useCallback
    
    useEffect(() => {
        const timer = setInterval(() => {
            setNextRefresh((prev) => (prev <= 1 ? 10 : prev - 1));
        }, 60000); // Update every minute
        return () => clearInterval(timer);
    }, []);

    if (loading) return (
        <div className="flex h-64 items-center justify-center flex-col gap-2">
            <RefreshCcw className="animate-spin text-blue-600" size={32} />
            <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Generating Intelligence...</p>
        </div>
    );

    return (
        <div className="-mt-3 space-y-3 p-3 max-w-[1600px] mx-auto bg-slate-50/50">
            {/* Header / Controls - Tightened padding */}
            <div className="flex justify-between items-center bg-white px-4 py-2.5 rounded-xl shadow-sm border border-slate-100">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-1.5 rounded-lg text-white">
                        <LayoutDashboard size={18} />
                    </div>
                    <div>
                        <h1 className="text-lg font-black text-slate-800 leading-none">Executive Dashboard 
                            <span className="text-lg font-black text-emerald-600 uppercase"> (FY{currentFYStart}/{currentFYStart + 1})</span></h1>
                    </div>
                </div>
                <span className="text-[12px] font-black text-slate-700 uppercase">
                    [ Next Dashboard Refresh is in: <span className="text-red-500">{nextRefresh} {nextRefresh !== 1 ? 'minutes' : 'minute'}</span> ]
                </span>
                <button 
                    onClick={() => exportDashboardToPDF(dashboardRef, `Revenue_Exec_DB_FY${currentFYStart}`)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white text-[10px] font-black rounded-lg hover:bg-slate-800 active:scale-95 shadow-md"
                >
                    <FileDown size={14} /> EXPORT PDF
                </button>
            </div>

            <div ref={dashboardRef} className="space-y-3">
                {/* 1. KPI Scorecards - Reduced gaps and padding */}
                <div className="grid grid-cols-8 lg:grid-cols-16 gap-2">
                    <KPIScorecard title="Current FY Revenue (FY To Date)" value={currentTotal}  trend={variance >= 0 ? 1 : -1} trendtitle = "Current FY vs Previous FY" trendValue={variance} icon={<TrendingUp size={16}/>} color="blue" isMoney />
                    <KPIScorecard title="Previous FY Revenue (FULL FY)" value={previousTotal} icon={<History size={16}/>} color="purple" isMoney />
                    <KPIScorecard title="Global Performance" value={`${globalPerformance.toFixed(1)}%`} target={`TGT: KES ${Number(globalTarget.toFixed(0) || 0).toLocaleString()}`} trend={globalPerformance >= 100 ? 1 : -1} trendtitle="Current FY vs Target" trendValue={globalPerformance} icon={<Target size={16}/>} color={`${getBarColorOnly(globalPerformance)}`} />
                    <KPIScorecard title="Sub Counties" value={scRankings.length} icon={<Map size={16}/>} color="indigo" isSmall />
                    <KPIScorecard title="Revenue Streams" value={AllStreams.length} icon={<Map size={16}/>} color="indigo" isSmall />
                </div>

                {/* 2. Main Visuals - Reduced chart height */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <div className="lg:col-span-2 bg-white px-4 py-3 rounded-xl shadow-sm border border-slate-100">
                        <h3 className="text-[10px] font-black text-slate-700 mb-3 uppercase tracking-wider">FY Revenue Trajectory -- 
                        <span className="text-[10px] font-black text-blue-700 mb-3 uppercase tracking-wider">Current </span>
                        <span className="text-[10px] font-bold text-slate-400 mb-3 tracking-wider">Vs </span>
                        <span className="text-[10px] font-bold text-purple-700 mb-3 uppercase tracking-wider">Previous</span>
                        </h3>
                        <div className="h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={processChartData(reportData, reportDataPrev)}>
                                    <defs>
                                        <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.1}/>
                                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 9, fontWeight: 'bold'}} />
                                    <YAxis hide />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area type="monotone" dataKey="current" stroke="#2563eb" strokeWidth={3} fill="url(#colorRev)" />
                                    <Area type="monotone" dataKey="previous" stroke="#a306f7" strokeWidth={3} fill="url(#colorRev)" strokeDasharray="4 4" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="bg-white px-4 py-3 rounded-xl shadow-sm border border-slate-100 flex flex-col">
                        <h3 className="text-[12px] font-black text-slate-700 mb-3 uppercase tracking-wider">
                            Ranked Revenue Streams
                        </h3>
                        
                        {/* Scrollable Container - Height set to match trajectory chart */}
                        {/* Scrollable Container - Height set to match trajectory chart */}
                        <div className="space-y-3 overflow-y-auto flex-1 pr-2 custom-scrollbar" style={{ maxHeight: '200px' }}>
                            {AllStreams.map((s, i) => {
                                // Calculate previous FY amount for this specific stream
                                const prevStreamData = reportDataPrev[s.name] || [];
                                const prevAmount = prevStreamData.reduce((sum, r) => sum + Number(r.amount_fy0 || 0), 0);

                                return (
                                    <div key={s.name} className="group">
                                        <div className="flex justify-between text-[12.5px] font-bold mb-0.5">
                                            <span className="text-slate-600 word-break mr-2 group-hover:text-blue-600 transition-colors">
                                                {s.name}
                                            </span>
                                            <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                                {/* Current FY Amount */}
                                                <span className="text-slate-900">
                                                    KES {s.amount.toLocaleString()}
                                                </span>
                                                {/* Previous FY Amount in brackets, small, and purple */}
                                                <span className="text-[10px] text-purple-600 font-bold">
                                                    ({prevAmount.toLocaleString()})
                                                </span>
                                            </div>
                                        </div>
                                        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-blue-600 rounded-full transition-all duration-500" 
                                                style={{ width: `${s.percentage}%` }} 
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* 3. Sub-County Leaderboard - More compact cards */}
                <div>
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Sub County Rankings </h3>
                        <span className="text-[11px] font-black text-slate-500">(Performance: Actual vs Target)</span>
                        <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                        {scRankings.map((sc, i) => (
                            <div key={sc.id} className="bg-white p-3 rounded-xl shadow-sm border border-slate-300 group hover:border-blue-400 transition-all">
                                <div className="flex justify-between items-center mb-1.5">
                                    <span className="text-[12px] font-black bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">#{i+1}</span>
                                    <Map size={12} className="text-slate-300 group-hover:text-blue-500" />
                                    {sc.staff && sc.staff.length > 0 ? (
                                        sc.staff.map((name, idx) => (
                                            <span key={idx} className="text-[12px] font-black text-slate-500 bg-blue-50 px-1.5 py-0.5 rounded uppercase border border-blue-100">
                                                RO: {name}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-[12px] font-bold text-slate-300 uppercase italic tracking-tighter">NO RO ASSIGNED</span>
                                    )}
                                </div>
                               <h4 className="flex justify-between items-center">
                                    <span className="text-sm font-bold text-slate-800 word-break leading-none mb-1">
                                        {sc.name}
                                    </span>
                                    <span className="text-sm font-black text-green-800 uppercase tracking-widest">
                                        ACT: KES {sc.actual.toLocaleString()}
                                    </span>
                                    </h4>
                                <div className="flex justify-between items-center">
                                    <span className={`text-lg font-black leading-none mb-2 ${getHeatmapClass(sc.performance).split(' ')[0]}`}>{sc.performance.toFixed(1)}%</span>
                                    <span className="text-[12px] font-black text-blue-600 uppercase tracking-widest">TGT: KES {sc.target.toLocaleString()}</span>
                                </div>
                                <div className="space-y-1.5">
                                    <div className="h-1 w-full bg-slate-50 rounded-full overflow-hidden">
                                        <div className={`h-full ${getBarColor(sc.performance)}`} style={{width: `${Math.min(sc.performance, 100)}%`}} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Helper Sub-Components ---

const KPIScorecard = ({ title, value, target, trend, trendtitle,trendValue, icon, color, isMoney, isSmall }) => (
    <div className={`
        bg-white rounded-xl border-b-2 border-${color}-500 shadow-sm
        ${isSmall ? 'col-span-2 lg:col-span-1 px-2 py-2.5' : 'col-span-3 lg:col-span-2 px-4 py-2.5'}
    `}>
        <div className="flex justify-between items-center text-slate-400 mb-1.5">
            <div className={`p-1 rounded-lg bg-${color}-50 text-${color}-600`}>{icon}</div>
            {trendValue !== undefined && trendtitle && (
                <div className={`flex items-center text-[10px] font-black ${trend >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {trendtitle && <span className="mr-1 text-black">{trendtitle}</span>}
                    {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {Math.abs(trendValue).toFixed(1)}%
                </div>
            )}
        </div>
        <p className="text-[12px] font-black text-slate-600 uppercase tracking-tighter">{title}</p>
        <div className="flex justify-between items-center">
            <span className={`text-lg font-black text-${color}-600 leading-none mb-2`}> 
                {isMoney ? `KES ${Number(value || 0).toLocaleString()}` : value}
                </span>
            {target && (
                <span className="text-[13px] font-black text-blue-600 uppercase tracking-widest">
                     {target}  
                </span>
            )}
        </div>
    </div>
);

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-900 px-2 py-1.5 rounded-lg shadow-xl border border-slate-800">
                <p className="text-[8px] font-black text-slate-400 uppercase mb-1">{payload[0].payload.monthFull}</p>
                <div className="space-y-0.5">
                    <p className="text-[10px] font-bold text-blue-400">Cur: {payload[0].value.toLocaleString()}</p>
                    <p className="text-[10px] font-bold text-slate-400">Prv: {payload[1].value.toLocaleString()}</p>
                </div>
            </div>
        );
    }
    return null;
};

const processChartData = (reportData, reportDataPrev) => {
    const months = ['July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June'];
    return months.map(m => {
        const records = Object.values(reportData).flat().filter(r => r.calendar_month === m);
        const recordsPrev = Object.values(reportDataPrev).flat().filter(r => r.calendar_month === m);
        return {
            month: m.substring(0, 3),
            monthFull: m,
            current: records.reduce((sum, r) => sum + Number(r.amount_fy0 || 0), 0),
            previous: recordsPrev.reduce((sum, r) => sum + Number(r.amount_fy0 || 0), 0)
        };
    });
};

export default RevenueExecutiveDashboard;